
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Download, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import EcommerceUploadTemplate from './EcommerceUploadTemplate';
import EcommercePreviewTable from './EcommercePreviewTable';
import { useEcommerceExcelParser } from './useEcommerceExcelParser';
import { CreateEcommercePriceInput } from '@/types/database/ecommerce-prices';

interface EcommerceUploadProps {
  onSuccess: () => void;
}

const EcommerceUpload: React.FC<EcommerceUploadProps> = ({ onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<CreateEcommercePriceInput[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { toast } = useToast();
  const { parseExcel, isLoading: isParsing, error: parseError } = useEcommerceExcelParser();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParsedData([]);
    setUploadStatus('idle');

    try {
      const data = await parseExcel(selectedFile);
      setParsedData(data);
    } catch (error) {
      console.error('Error parsing file:', error);
      toast({
        title: 'Error',
        description: 'Failed to parse the uploaded file',
        variant: 'destructive',
      });
    }
  };

  const handleUpload = async () => {
    if (parsedData.length === 0) return;

    setIsUploading(true);
    setUploadStatus('idle');

    try {
      const { error } = await supabase
        .from('ecommerce_prices')
        .insert(parsedData);

      if (error) throw error;

      setUploadStatus('success');
      toast({
        title: 'Success',
        description: `Successfully uploaded ${parsedData.length} ecommerce prices`,
      });

      // Reset form
      setFile(null);
      setParsedData([]);
      if (document.querySelector('input[type="file"]') as HTMLInputElement) {
        (document.querySelector('input[type="file"]') as HTMLInputElement).value = '';
      }

      onSuccess();
    } catch (error) {
      console.error('Error uploading data:', error);
      setUploadStatus('error');
      toast({
        title: 'Error',
        description: 'Failed to upload ecommerce prices',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Upload Ecommerce Prices</h2>
        <p className="text-gray-600">
          Upload multiple ecommerce prices from an Excel file
        </p>
      </div>

      <EcommerceUploadTemplate />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Excel File
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <FileText className="w-8 h-8 mb-4 text-gray-500" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> your Excel file
                </p>
                <p className="text-xs text-gray-500">Excel files only (.xlsx, .xls)</p>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
              />
            </label>
          </div>

          {file && (
            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription>
                Selected file: {file.name}
              </AlertDescription>
            </Alert>
          )}

          {parseError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{parseError}</AlertDescription>
            </Alert>
          )}

          {uploadStatus === 'success' && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Ecommerce prices uploaded successfully!
              </AlertDescription>
            </Alert>
          )}

          {uploadStatus === 'error' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to upload ecommerce prices. Please try again.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {parsedData.length > 0 && (
        <EcommercePreviewTable data={parsedData} />
      )}

      {parsedData.length > 0 && (
        <div className="flex justify-end gap-4">
          <Button
            onClick={handleUpload}
            disabled={isUploading || isParsing}
            size="lg"
          >
            {isUploading ? 'Uploading...' : `Upload ${parsedData.length} Prices`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EcommerceUpload;
